<!-- Generic 404 Error Page -->
<div *ngIf="datasetNotFound" class="min-h-screen flex items-center justify-center bg-gray-100">
  <div class="text-center">
    <h1 class="text-6xl font-bold text-gray-400 mb-4">404</h1>
    <p class="text-xl text-gray-600">Page not found</p>
  </div>
</div>

<!-- Main Chat Interface -->
<div *ngIf="!datasetNotFound" class="flex flex-col h-screen bg-gradient-to-br from-green-50 to-emerald-50">
  <div class="flex flex-col max-w-4xl mx-auto w-full px-4 py-4 h-full">
    <!-- Title Header -->
    <div *ngIf="dataset?.title" class="mb-4 text-center flex-shrink-0">
      <div class="flex items-center justify-center gap-3">
        <img src="assets/telly/AskTellyHeaderImageTransparentCentered.png" alt="Ask Telly" class="w-32 h-32 rounded-2xl" />
        <h1 class="text-3xl font-bold text-gray-800">{{ dataset?.title }}</h1>
        <button
          *ngIf="dataset?.description"
          (click)="openDescriptionDialog()"
          class="w-7 h-7 rounded-full bg-[#0F5E2D] hover:bg-[#17A34A] text-white flex items-center justify-center text-sm font-bold transition-colors duration-200 cursor-pointer"
          title="About this dataset"
        >
          i
        </button>
      </div>
    </div>

    <!-- Description Dialog -->
    <p-dialog
      header="About {{ dataset?.title }}"
      [(visible)]="showDescriptionDialog"
      [modal]="true"
      [style]="{ width: '500px', maxWidth: '90vw' }"
      [draggable]="false"
      [resizable]="false"
      styleClass="description-dialog"
    >
      <div class="prose prose-sm max-w-none" [innerHTML]="getFormattedDescription()"></div>
    </p-dialog>

    <!-- Image Preview Dialog -->
    <p-dialog
      [(visible)]="showGalleria"
      [modal]="true"
      [dismissableMask]="true"
      [style]="{width: '90vw', maxWidth: '1200px'}"
      [draggable]="false"
      [resizable]="false"
      styleClass="image-preview-dialog"
      (onHide)="closeGallery()"
    >
      <ng-template pTemplate="header">
        <span class="text-lg font-medium">{{ getSelectedImage()?.filename || 'Image Preview' }}</span>
      </ng-template>
      <div class="flex flex-col items-center">
        <img
          *ngIf="getSelectedImage()"
          [src]="getSelectedImage()?.url"
          [alt]="getSelectedImage()?.description || 'Image'"
          class="max-w-full max-h-[70vh] object-contain rounded"
        />
        <p *ngIf="getSelectedImage()?.description" class="mt-3 text-sm text-gray-600 text-center">
          {{ getSelectedImage()?.description }}
        </p>
        <!-- Navigation buttons for multiple images -->
        <div *ngIf="activeGalleriaImages.length > 1" class="flex items-center justify-center gap-4 mt-4">
          <button
            (click)="prevImage()"
            class="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
          >
            <i class="pi pi-chevron-left"></i> Previous
          </button>
          <span class="text-gray-600">{{ selectedImageIndex + 1 }} / {{ activeGalleriaImages.length }}</span>
          <button
            (click)="nextImage()"
            class="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
          >
            Next <i class="pi pi-chevron-right"></i>
          </button>
        </div>
      </div>
    </p-dialog>

    <!-- Chat Card - Flex container -->
    <div class="flex-1 flex flex-col shadow-lg rounded-lg bg-white overflow-hidden border-t-4 border-[#17A34A]" style="min-height: 300px;">
      <!-- Messages Container -->
      <div
        #messagesContainer
        class="flex-1 overflow-y-auto p-4 space-y-4"
      >
        <!-- Welcome Message -->
        <div *ngIf="messages.length === 0" class="text-center py-6">
          <img src="assets/telly/telly-wave-heart.png" alt="Telly waving" class="w-24 h-24 mx-auto mb-4" />
          <h2 class="text-2xl font-semibold text-gray-700 mb-2">Hi! I'm Telly</h2>
          <p class="text-gray-500 mb-4">Ask me anything or choose a conversation starter</p>

          <!-- Conversation Starters -->
          <div *ngIf="conversationStarters.length > 0" class="max-w-2xl mx-auto">
            <p class="text-sm font-semibold text-gray-700 mb-2">Suggested Questions:</p>
            <div class="border-2 border-[#17A34A] rounded-lg p-4 bg-[#0F5E2D] space-y-3">
              <button
                *ngFor="let starter of conversationStarters"
                (click)="useConversationStarter(starter)"
                class="w-full text-left px-5 py-4 bg-white hover:bg-green-50 text-gray-800 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md flex items-start group"
              >
                <i class="pi pi-comment text-[#17A34A] mr-3 mt-0.5 group-hover:text-[#0F5E2D]"></i>
                <span class="flex-1">{{ starter }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Messages -->
        <div
          *ngFor="let message of messages"
          class="flex"
          [class.justify-start]="message.messageType === 'user'"
          [class.justify-end]="message.messageType === 'assistant'"
        >
          <div
            class="max-w-[80%] rounded-lg p-4"
            [ngClass]="{
              'bg-gray-100 text-gray-800 border border-gray-200': message.messageType === 'user',
              'bg-[rgba(22,163,74,0.85)] text-white': message.messageType === 'assistant'
            }"
          >
            <p class="whitespace-pre-wrap break-words">{{ message.content }}</p>

            <!-- Image thumbnails for assistant messages -->
            <div *ngIf="message.messageType === 'assistant' && hasImages(message.id)" class="mt-3 pt-3 border-t border-white/20">
              <p class="text-xs text-white mb-2">Related Images:</p>
              <div class="flex flex-wrap gap-2">
                <div
                  *ngFor="let image of getMessageImages(message.id); let i = index"
                  class="cursor-pointer relative group"
                  (click)="openGallery(message.id, i)"
                >
                  <img
                    [src]="image.url"
                    [alt]="image.description || 'Image ' + (i + 1)"
                    class="w-20 h-20 object-cover rounded-md border border-white/50 transition-all duration-200 group-hover:border-white group-hover:shadow-md"
                  />
                  <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 rounded-md flex items-center justify-center">
                    <i class="pi pi-search-plus text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200"></i>
                  </div>
                </div>
              </div>
            </div>

            <!-- Metadata for assistant messages -->
            <div *ngIf="message.messageType === 'assistant' && message.confidenceScore !== undefined" class="mt-3 pt-3 border-t border-white/20">
              <div class="flex items-center justify-between text-xs">
                <div class="flex items-center space-x-2">
                  <span class="text-white">Confidence:</span>
                  <p-chip
                    [label]="(message.confidenceScore * 100).toFixed(0) + '%'"
                    [ngClass]="getConfidenceClass(message.confidenceScore)"
                    styleClass="text-xs px-2 py-1"
                  ></p-chip>
                </div>
                <span *ngIf="message.responseTimeMs" class="text-white">
                  {{ message.responseTimeMs }}ms
                </span>
              </div>
            </div>
          </div>
          <!-- Telly bubble avatar for assistant messages -->
          <img
            *ngIf="message.messageType === 'assistant'"
            src="assets/telly/AskTellyBubble.png"
            alt="Telly"
            class="w-8 h-8 rounded-full ml-2 flex-shrink-0 self-end"
          />
        </div>

        <!-- Typing Indicator (like messenger) -->
        <div *ngIf="isLoading" class="flex justify-end">
          <div class="typing-indicator scale-90">
            <div class="dot"></div>
            <div class="dot"></div>
            <div class="dot"></div>
          </div>
          <img src="assets/telly/AskTellyBubble.png" alt="Telly" class="w-8 h-8 rounded-full ml-2 flex-shrink-0 self-end" />
        </div>

      </div>

      <!-- Input Form - Pinned to bottom -->
      <div class="flex-shrink-0 border-t border-gray-200 bg-white p-4">
        <form [formGroup]="chatForm" (ngSubmit)="sendMessage()" class="space-y-3">
          <div class="flex items-center space-x-2">
            <input
              type="text"
              pInputText
              formControlName="message"
              placeholder="Ask Telly a question..."
              class="flex-1 message-input transition-all duration-200"
              [disabled]="isLoading"
              (keydown.enter)="sendMessage(); $event.preventDefault()"
            />
            <p-button
              type="submit"
              icon="pi pi-send"
              [disabled]="chatForm.invalid || isLoading"
              [loading]="isLoading"
              label="Send"
              styleClass="telly-send-btn"
            ></p-button>
            <p-button
              type="button"
              icon="pi pi-refresh"
              severity="secondary"
              [disabled]="messages.length === 0 || isLoading"
              (onClick)="clearConversation()"
              label="Clear"
              [outlined]="true"
            ></p-button>
          </div>
        </form>

        <!-- Footer -->
        <div class="mt-3 text-center">
          <div class="text-xs text-gray-500 mb-2">
            <span>UI v{{ uiVersion }}</span>
            <span class="mx-2">|</span>
            <span>RAG v{{ dataset?.ragVersion ?? 0 }}</span>
          </div>
          <img src="assets/telly/infocision-logo.svg" alt="InfoCision" class="h-5 mx-auto opacity-60" />
        </div>
      </div>
    </div>
  </div>
</div>
